/**
 * FormBuilder — the editor used by SA + tenant admins to create/modify
 * a FormDefinition. Three columns:
 *   1. Field-type palette (left) — click to append
 *   2. Canvas (centre) — list of fields in order; click to select
 *   3. Properties panel (right) — edits the selected field's label,
 *      placeholder, validation rules, options
 *
 * Reorder is via simple up/down buttons (drag-drop polish in next pass).
 * Save is the parent's responsibility — this component only emits
 * `onChange` with the latest list of FormFields.
 */
import * as React from "react";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import {
  FIELD_TYPE_LABEL,
  FIELD_TYPE_ORDER,
  type FieldType,
  type FieldValidation,
  type FormField,
} from "@eduviaos/shared/types";
import { SLUG_RULES, deriveSlug, uniqueSlug } from "@eduviaos/shared/validation";
import { Button } from "../components/button.js";
import { Card, CardContent } from "../components/card.js";
import { Checkbox } from "../components/checkbox.js";
import { Input } from "../components/input.js";
import { Label } from "../components/label.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/select.js";
import { Textarea } from "../components/textarea.js";
import { cn } from "../lib/cn.js";

interface FormBuilderProps {
  fields: FormField[];
  onChange: (fields: FormField[]) => void;
}

const TYPES_NEEDING_OPTIONS: ReadonlySet<FieldType> = new Set([
  "select",
  "multi_select",
  "radio",
  "checkbox",
]);

/**
 * Submission key for a field labelled `label`, unique among `taken`
 * (`full_name`, `full_name_2`, …). Keys are never shown or typed; they
 * satisfy the backend `^[a-z][a-z0-9_]*$` rule by construction.
 */
function keyForLabel(label: string, taken: Iterable<string>): string {
  const base = deriveSlug(label, SLUG_RULES.formFieldKey) || "field";
  return uniqueSlug(base, taken, SLUG_RULES.formFieldKey);
}

export function FormBuilder({ fields, onChange }: FormBuilderProps) {
  const [selectedIdx, setSelectedIdx] = React.useState<number | null>(fields.length ? 0 : null);
  // Keys of fields added in this editing session. Their key follows the
  // label until saved; keys that arrived with the form never change,
  // because stored submissions are keyed by them.
  const [freshKeys, setFreshKeys] = React.useState<ReadonlySet<string>>(() => new Set());

  const addField = (type: FieldType) => {
    const key = keyForLabel(
      FIELD_TYPE_LABEL[type],
      fields.map((f) => f.key),
    );
    setFreshKeys((prev) => new Set(prev).add(key));
    const defaultOptions = TYPES_NEEDING_OPTIONS.has(type)
      ? [
          { value: "option_1", label: "Option 1" },
          { value: "option_2", label: "Option 2" },
        ]
      : null;
    const newField: FormField = {
      key,
      label: FIELD_TYPE_LABEL[type],
      field_type: type,
      validation: defaultOptions ? { options: defaultOptions } : {},
      grid_cols: type === "section_header" || type === "help_text" ? 4 : 2,
      order: fields.length,
    };
    onChange([...fields, newField]);
    setSelectedIdx(fields.length);
  };

  const updateAt = (idx: number, patch: Partial<FormField>) => {
    const target = fields[idx];
    if (!target) return;
    const next = fields.slice();
    next[idx] = { ...target, ...patch };
    onChange(next);
  };

  const updateLabelAt = (idx: number, label: string) => {
    const target = fields[idx];
    if (!target) return;
    if (!freshKeys.has(target.key)) {
      updateAt(idx, { label });
      return;
    }
    const key = keyForLabel(
      label,
      fields.filter((_, i) => i !== idx).map((f) => f.key),
    );
    if (key !== target.key) {
      setFreshKeys((prev) => {
        const next = new Set(prev);
        next.delete(target.key);
        next.add(key);
        return next;
      });
    }
    updateAt(idx, { label, key });
  };

  const updateValidationAt = (idx: number, patch: Partial<FieldValidation>) => {
    const target = fields[idx];
    if (!target) return;
    const next = fields.slice();
    const v = target.validation ?? {};
    next[idx] = { ...target, validation: { ...v, ...patch } };
    onChange(next);
  };

  const removeAt = (idx: number) => {
    const next = fields
      .filter((_, i) => i !== idx)
      .map((f, i) => ({
        ...f,
        order: i,
      }));
    onChange(next);
    if (selectedIdx === idx) setSelectedIdx(null);
    else if (selectedIdx !== null && selectedIdx > idx) setSelectedIdx(selectedIdx - 1);
  };

  const moveAt = (idx: number, dir: -1 | 1) => {
    const targetIdx = idx + dir;
    if (targetIdx < 0 || targetIdx >= fields.length) return;
    const a = fields[idx];
    const b = fields[targetIdx];
    if (!a || !b) return;
    const next = fields.slice();
    next[idx] = b;
    next[targetIdx] = a;
    next.forEach((f, i) => (f.order = i));
    onChange(next);
    setSelectedIdx(targetIdx);
  };

  const selected = selectedIdx !== null ? (fields[selectedIdx] ?? null) : null;

  return (
    <div className="grid gap-4 lg:grid-cols-[200px_1fr_320px]">
      {/* Palette */}
      <Card>
        <CardContent className="p-3">
          <h4 className="text-muted-foreground mb-2 text-xs font-semibold uppercase tracking-wide">
            Add field
          </h4>
          <div className="grid gap-1">
            {FIELD_TYPE_ORDER.map((t) => (
              <Button
                key={t}
                variant="ghost"
                size="sm"
                className="h-8 justify-start text-xs"
                onClick={() => addField(t)}
              >
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                {FIELD_TYPE_LABEL[t]}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Canvas */}
      <Card>
        <CardContent className="p-4">
          {fields.length === 0 ? (
            <div className="border-border text-muted-foreground flex h-40 items-center justify-center rounded-md border border-dashed text-sm">
              Click a field type on the left to add it.
            </div>
          ) : (
            <div className="grid gap-2">
              {fields.map((f, i) => (
                <div
                  key={`${f.key}-${i}`}
                  onClick={() => setSelectedIdx(i)}
                  className={cn(
                    "bg-background group flex cursor-pointer items-center justify-between rounded-md border px-3 py-2 transition-colors",
                    selectedIdx === i
                      ? "border-primary ring-primary ring-1"
                      : "border-border hover:bg-muted/40",
                  )}
                >
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{f.label}</span>
                    <span className="text-muted-foreground text-xs">
                      {FIELD_TYPE_LABEL[f.field_type]}
                      {f.validation?.required && " · required"}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 opacity-70 transition-opacity group-hover:opacity-100">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        moveAt(i, -1);
                      }}
                      disabled={i === 0}
                    >
                      <ArrowUp className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        moveAt(i, 1);
                      }}
                      disabled={i === fields.length - 1}
                    >
                      <ArrowDown className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeAt(i);
                      }}
                    >
                      <Trash2 className="text-destructive h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Properties */}
      <Card>
        <CardContent className="p-4">
          {selected === null || selectedIdx === null ? (
            <div className="text-muted-foreground flex h-40 items-center justify-center text-center text-sm">
              Select a field to edit its properties.
            </div>
          ) : (
            <div className="grid gap-3">
              <h4 className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
                Field properties
              </h4>

              <div className="grid gap-1.5">
                <Label htmlFor="prop-label">Label</Label>
                <Input
                  id="prop-label"
                  value={selected.label}
                  maxLength={200}
                  onChange={(e) => updateLabelAt(selectedIdx, e.target.value)}
                />
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="prop-type">Type</Label>
                <Select
                  value={selected.field_type}
                  onValueChange={(val) => {
                    const next = val as FieldType;
                    const patch: Partial<FormField> = { field_type: next };
                    if (TYPES_NEEDING_OPTIONS.has(next) && !selected.validation?.options?.length) {
                      patch.validation = {
                        ...(selected.validation ?? {}),
                        options: [
                          { value: "option_1", label: "Option 1" },
                          { value: "option_2", label: "Option 2" },
                        ],
                      };
                    }
                    updateAt(selectedIdx, patch);
                  }}
                >
                  <SelectTrigger id="prop-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPE_ORDER.map((t) => (
                      <SelectItem key={t} value={t}>
                        {FIELD_TYPE_LABEL[t]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="prop-placeholder">Placeholder</Label>
                <Input
                  id="prop-placeholder"
                  value={selected.placeholder ?? ""}
                  onChange={(e) =>
                    updateAt(selectedIdx, {
                      placeholder: e.target.value || null,
                    })
                  }
                />
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="prop-help">Help text</Label>
                <Textarea
                  id="prop-help"
                  rows={2}
                  value={selected.help_text ?? ""}
                  onChange={(e) =>
                    updateAt(selectedIdx, {
                      help_text: e.target.value || null,
                    })
                  }
                />
              </div>

              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <Checkbox
                  checked={!!selected.validation?.required}
                  onCheckedChange={(state) =>
                    updateValidationAt(selectedIdx, { required: state === true })
                  }
                />
                Required field
              </label>

              {TYPES_NEEDING_OPTIONS.has(selected.field_type) && (
                <div className="grid gap-1.5">
                  <Label>Options</Label>
                  <div className="grid gap-1.5">
                    {(selected.validation?.options ?? []).map((opt, i) => (
                      <div key={i} className="flex gap-1.5">
                        <Input
                          placeholder="value"
                          value={opt.value}
                          onChange={(e) => {
                            const opts = selected.validation?.options?.slice() ?? [];
                            const cur = opts[i];
                            if (!cur) return;
                            opts[i] = { ...cur, value: e.target.value };
                            updateValidationAt(selectedIdx, { options: opts });
                          }}
                          className="font-mono text-xs"
                        />
                        <Input
                          placeholder="Label shown to user"
                          value={opt.label}
                          onChange={(e) => {
                            const opts = selected.validation?.options?.slice() ?? [];
                            const cur = opts[i];
                            if (!cur) return;
                            opts[i] = { ...cur, label: e.target.value };
                            updateValidationAt(selectedIdx, { options: opts });
                          }}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            const next =
                              selected.validation?.options?.filter((_, j) => j !== i) ?? [];
                            updateValidationAt(selectedIdx, { options: next });
                          }}
                        >
                          <Trash2 className="text-destructive h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const opts = selected.validation?.options ?? [];
                        const nextIndex = opts.length + 1;
                        updateValidationAt(selectedIdx, {
                          options: [
                            ...opts,
                            {
                              value: `option_${nextIndex}`,
                              label: `Option ${nextIndex}`,
                            },
                          ],
                        });
                      }}
                    >
                      <Plus className="mr-1 h-3.5 w-3.5" />
                      Add option
                    </Button>
                  </div>
                </div>
              )}

              {selected.field_type === "file_upload" && (
                <>
                  <div className="grid gap-1.5">
                    <Label htmlFor="prop-mimes">Accepted MIME types (comma-separated)</Label>
                    <Input
                      id="prop-mimes"
                      placeholder="application/pdf, image/jpeg"
                      value={(selected.validation?.mime_types ?? []).join(", ")}
                      onChange={(e) =>
                        updateValidationAt(selectedIdx, {
                          mime_types: e.target.value
                            .split(",")
                            .map((s) => s.trim())
                            .filter(Boolean),
                        })
                      }
                    />
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor="prop-size">Max file size (MB)</Label>
                    <Input
                      id="prop-size"
                      type="number"
                      min={1}
                      max={200}
                      value={selected.validation?.max_file_size_mb ?? ""}
                      onChange={(e) =>
                        updateValidationAt(selectedIdx, {
                          max_file_size_mb: e.target.value ? Number(e.target.value) : null,
                        })
                      }
                    />
                  </div>
                </>
              )}

              {(selected.field_type === "text" || selected.field_type === "textarea") && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="grid gap-1.5">
                      <Label htmlFor="prop-min-length">Min length</Label>
                      <Input
                        id="prop-min-length"
                        type="number"
                        min={0}
                        value={selected.validation?.min_length ?? ""}
                        onChange={(e) =>
                          updateValidationAt(selectedIdx, {
                            min_length: e.target.value ? Number(e.target.value) : null,
                          })
                        }
                      />
                    </div>
                    <div className="grid gap-1.5">
                      <Label htmlFor="prop-max-length">Max length</Label>
                      <Input
                        id="prop-max-length"
                        type="number"
                        min={1}
                        value={selected.validation?.max_length ?? ""}
                        onChange={(e) =>
                          updateValidationAt(selectedIdx, {
                            max_length: e.target.value ? Number(e.target.value) : null,
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor="prop-pattern">Regex pattern (optional)</Label>
                    <Input
                      id="prop-pattern"
                      placeholder="^[A-Z]{5}\\d{4}[A-Z]$"
                      value={selected.validation?.pattern ?? ""}
                      onChange={(e) =>
                        updateValidationAt(selectedIdx, {
                          pattern: e.target.value || null,
                        })
                      }
                      className="font-mono text-xs"
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
